// loginClientInfo.js

const isBrowser =
  typeof window !== "undefined" && typeof navigator !== "undefined";

export function parseUserAgent(ua = isBrowser ? navigator.userAgent : "") {
  let browserName = "Unknown";
  let browserVersion = null;
  let os = "Unknown";
  let deviceType = "desktop";

  const browserRules = [
    { name: "Edge", regex: /Edg\/([\d.]+)/ },
    { name: "Opera", regex: /OPR\/([\d.]+)/ },
    { name: "Chrome", regex: /Chrome\/([\d.]+)/ },
    { name: "Firefox", regex: /Firefox\/([\d.]+)/ },
    { name: "Safari", regex: /Version\/([\d.]+).*Safari/ },
  ];

  for (const rule of browserRules) {
    const match = ua.match(rule.regex);
    if (match) {
      browserName = rule.name;
      browserVersion = match[1] || null;
      break;
    }
  }

  if (/Windows NT/i.test(ua)) os = "Windows";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Mac OS X|Macintosh/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";

  if (
    /iPad|Tablet|PlayBook|Silk/i.test(ua) ||
    (/Android/i.test(ua) && !/Mobile/i.test(ua))
  ) {
    deviceType = "tablet";
  } else if (/Mobile|iPhone|iPod|Android/i.test(ua)) {
    deviceType = "mobile";
  }

  return {
    userAgent: ua || null,
    browserName,
    browserVersion,
    os,
    deviceType,
  };
}

export function getBrowserDeviceInfo() {
  if (!isBrowser) {
    return {
      userAgent: null,
      browserName: null,
      browserVersion: null,
      os: null,
      deviceType: null,
    };
  }

  return parseUserAgent(navigator.userAgent);
}

export function getPlatformInfo() {
  if (!isBrowser) {
    return { platform: null };
  }

  return {
    platform: navigator.platform || null,
  };
}

export function getScreenInfo() {
  if (!isBrowser) {
    return {
      screenWidth: null,
      screenHeight: null,
    };
  }

  return {
    screenWidth: window.screen?.width ?? null,
    screenHeight: window.screen?.height ?? null,
  };
}

export function getNetworkInfo() {
  if (!isBrowser) {
    return {
      supported: false,
      online: null,
      type: null,
      effectiveType: null,
      downlink: null,
      rtt: null,
      saveData: null,
    };
  }

  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;

  return {
    supported: !!connection,
    online: typeof navigator.onLine === "boolean" ? navigator.onLine : null,
    type: connection?.type ?? null,
    effectiveType: connection?.effectiveType ?? null,
    downlink: connection?.downlink ?? null,
    rtt: connection?.rtt ?? null,
    saveData:
      typeof connection?.saveData === "boolean" ? connection.saveData : null,
  };
}

export async function getLocationInfo(options = {}) {
  const defaultOptions = {
    enableHighAccuracy: false,
    timeout: 2000,
    maximumAge: 300000,
  };

  if (!isBrowser) {
    return {
      supported: false,
      permissionState: null,
      coords: null,
      timestamp: null,
      error: "Not running in browser",
    };
  }

  let permissionState = "unknown";

  if (navigator.permissions?.query) {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "geolocation",
      });
      permissionState = permissionStatus.state;
    } catch {
      permissionState = "unknown";
    }
  }

  if (!("geolocation" in navigator)) {
    return {
      supported: false,
      permissionState,
      coords: null,
      timestamp: null,
      error: "Geolocation API not supported",
    };
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          supported: true,
          permissionState,
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude ?? null,
            altitudeAccuracy: position.coords.altitudeAccuracy ?? null,
            heading: position.coords.heading ?? null,
            speed: position.coords.speed ?? null,
          },
          timestamp: position.timestamp,
          error: null,
        });
      },
      (error) => {
        resolve({
          supported: true,
          permissionState,
          coords: null,
          timestamp: null,
          error: {
            code: error.code,
            message: error.message,
          },
        });
      },
      { ...defaultOptions, ...options }
    );
  });
}

export async function collectLoginClientInfo({
  includeGeolocation = false,
  geolocationOptions = {},
} = {}) {
  const location = includeGeolocation
    ? await getLocationInfo(geolocationOptions)
    : {
        supported: isBrowser ? "geolocation" in navigator : false,
        permissionState: "not_requested",
        coords: null,
        timestamp: null,
        error: null,
      };

  return {
    // collectedAt: new Date().toISOString(),

    browserDevice: getBrowserDeviceInfo(),

    platform: getPlatformInfo(),

    display: getScreenInfo(),

    location,

    network: getNetworkInfo(),
  };
}